<template>
  <div class="stock-overview">
    <div class="overview-header">
      <div class="stock-identity">
        <h2 class="stock-name">
          {{ stockName }}
          <span
            class="stock-link ml-10"
            @click="openStockDialog && openStockDialog({ symbol, stockName, sector })"
            title="View Stock Details"
          >
            {{ symbol }} <i class="pi pi-link"></i>
          </span>
        </h2>
        <div class="stock-meta">
          <span class="stock-sector">{{ sector }}</span>
        </div>
      </div>
      <div class="price-section">
        <div class="current-price">₹{{ formatDecimal(currentPrice) }}</div>
        <div class="price-label">Current Price</div>
      </div>
    </div>

    <div class="overview-grid">
      <div class="overview-card">
        <div class="card-header">
          <h3>Valuation Summary</h3>
          <div class="valuation-status" :class="mosClass">
            {{ mosLabel }}
          </div>
        </div>
        <div class="valuation-metrics">
          <div class="metric">
            <span class="metric-label">Intrinsic Value</span>
            <span class="metric-value">₹{{ formatDecimal(intrinsicValue) }}</span>
          </div>
          <div class="metric">
            <span class="metric-label">Margin of Safety</span>
            <span class="metric-value" :class="mosClass">{{ formatDecimal(marginOfSafety) }}%</span>
          </div>
        </div>
      </div>

      <div class="overview-card">
        <h3>Key Insights</h3>
        <div class="insights-list">
          <div v-for="insight in keyInsights" :key="insight.id" class="insight-item">
            <div class="insight-icon" :class="insight.type">
              <i :class="insight.icon"></i>
            </div>
            <div class="insight-content">
              <div class="insight-title">{{ insight.title }}</div>
              <div class="insight-description">{{ insight.description }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  symbol: {
    type: String,
    required: true
  },
  stockName: {
    type: String,
    required: true
  },
  sector: {
    type: String,
    required: true
  },
  currentPrice: {
    type: Number,
    required: true
  },
  intrinsicValue: {
    type: Number,
    required: true
  },
  marginOfSafety: {
    type: Number,
    required: true
  },
  openStockDialog: Function
});

const mosClass = computed(() => {
  if (props.marginOfSafety > 10) return 'positive';
  if (props.marginOfSafety < -10) return 'negative';
  return 'neutral';
});

const mosLabel = computed(() => {
  if (props.marginOfSafety > 10) return 'Undervalued';
  if (props.marginOfSafety < -10) return 'Overvalued';
  return 'Fair Value';
});

const keyInsights = computed(() => {
  const insights = [];

  // Valuation insight
  if (props.marginOfSafety > 15) {
    insights.push({
      id: 'valuation-positive',
      type: 'positive',
      icon: 'pi pi-arrow-up',
      title: 'Strong Value Opportunity',
      description: `Stock appears undervalued by ${props.marginOfSafety.toFixed(1)}%`
    });
  } else if (props.marginOfSafety < -15) {
    insights.push({
      id: 'valuation-negative',
      type: 'negative',
      icon: 'pi pi-arrow-down',
      title: 'Potentially Overvalued',
      description: `Stock may be overvalued by ${Math.abs(props.marginOfSafety).toFixed(1)}%`
    });
  } else {
    insights.push({
      id: 'valuation-neutral',
      type: 'neutral',
      icon: 'pi pi-minus',
      title: 'Fair Value Range',
      description: 'Stock is trading near its intrinsic value'
    });
  }

  // Price momentum insight
  insights.push({
    id: 'momentum',
    type: 'neutral',
    icon: 'pi pi-chart-line',
    title: 'Technical Analysis',
    description: 'View detailed technical indicators in the Technicals tab'
  });

  // Portfolio impact insight
  insights.push({
    id: 'portfolio',
    type: 'neutral',
    icon: 'pi pi-briefcase',
    title: 'Portfolio Impact',
    description: 'Analyze how adding this stock affects your portfolio'
  });

  return insights;
});

const formatDecimal = (value, digits = 2) => {
  if (value === null || value === undefined || isNaN(value)) return '0.00';
  return Number(value).toFixed(digits);
};
</script>

<style scoped>
.stock-overview {
  max-width: 1000px;
}

.overview-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.stock-identity {
  flex: 1;
}

.stock-name {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 6px 0;
}

.stock-link {
  cursor: pointer;
  color: #065f46;
  font-weight: 600;
  background: #e6f4f1;
  border-radius: 8px;
  padding: 2px 8px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: background 0.2s;
  font-size: 0.95rem;
}

.stock-link:hover {
  background: #d1fae5;
  text-decoration: underline;
}

.ml-10 {
  margin-left: 10px;
}

.stock-meta {
  display: flex;
  gap: 12px;
  align-items: center;
}

.stock-symbol {
  font-size: 0.85rem;
  font-weight: 600;
  color: #3b82f6;
  background: #eff6ff;
  padding: 3px 6px;
  border-radius: 3px;
}

.stock-sector {
  font-size: 0.8rem;
  color: #6b7280;
}

.price-section {
  text-align: right;
}

.current-price {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
}

.price-label {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 3px;
}

.overview-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.overview-card {
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
}

.overview-card h3 {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 12px 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.valuation-status {
  padding: 3px 6px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
}

.valuation-status.positive {
  background: #dcfce7;
  color: #166534;
}

.valuation-status.negative {
  background: #fef2f2;
  color: #dc2626;
}

.valuation-status.neutral {
  background: #f3f4f6;
  color: #374151;
}

.valuation-metrics {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.metric {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.metric-label {
  font-size: 0.8rem;
  color: #6b7280;
}

.metric-value {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f2937;
}

.metric-value.positive {
  color: #059669;
}

.metric-value.negative {
  color: #dc2626;
}

.insights-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.insight-item {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.insight-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.insight-icon.positive {
  background: #dcfce7;
  color: #166534;
}

.insight-icon.negative {
  background: #fef2f2;
  color: #dc2626;
}

.insight-icon.neutral {
  background: #f3f4f6;
  color: #6b7280;
}

.insight-content {
  flex: 1;
}

.insight-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 2px;
}

.insight-description {
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.4;
}

@media (max-width: 768px) {
  .overview-header {
    flex-direction: column;
    gap: 16px;
  }

  .overview-grid {
    grid-template-columns: 1fr;
  }

  .stock-name {
    font-size: 1.3rem;
  }

  .current-price {
    font-size: 1.5rem;
  }

  .overview-card {
    padding: 16px;
  }
}
</style>
